import * as THREE from "three";
import React, { useMemo } from "react";
import { floorplannerStore } from "../../store/floorplannerStore";
import { WallConnectionEnd, WallConnectionStart, WallType } from "../../types/floorplannerTypes";
import { editorStore } from "../../store/editorStore";
import performanceMonitor from "../../utils/PerformanceMonitor";
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";

interface AttachmentPoint {
  id: string;
  position: THREE.Vector2;
  type: 'wall' | 'endpoint';
  visible: boolean;
}

interface AttachmentIndicatorProps {
  position: THREE.Vector2;
  visible: boolean;
  type: 'wall' | 'endpoint';
}

/**
 * Visual attachment point indicator component for drag operations
 * Shows where walls can attach during drag operations with performance-aware rendering
 */
export const AttachmentIndicator: React.FC<AttachmentIndicatorProps> = ({ position, visible, type }) => {
  // Calculate indicator color based on type
  const color = useMemo(() => {
    return type === 'wall' ? CANVAS_ACCENT_COLOR : '#10B981'; // Brand yellow for walls, green for endpoints
  }, [type]);

  // Calculate indicator size based on zoom level
  const indicatorSize = useMemo(() => {
    const zoom = editorStore.zoomLevel || 1.0;
    return Math.max(8, 15 / zoom); // Scale inversely with zoom
  }, []);

  // Early return check must come AFTER all hooks are called
  if (!visible) return null;

  return (
    <mesh position={[position.x, position.y, 0.1]}>
      <circleGeometry args={[indicatorSize / 1000, 16]} />
      <meshBasicMaterial
        color={color}
        opacity={0.6}
        transparent
        depthWrite={false}
      />
    </mesh>
  );
};

/**
 * Hook for detecting and managing attachment points during drag operations
 */
export const useAttachmentIndicators = (wallId: string, dragPosition: THREE.Vector2 | null): AttachmentPoint[] => {
  return useMemo(() => {
    if (!dragPosition) return [];

    // Check if performance allows visual indicators
    const shouldShowIndicators = performanceMonitor.shouldShowAttachmentIndicators();
    if (!shouldShowIndicators) return [];

    const attachmentPoints: AttachmentPoint[] = [];
    const sourceWall = floorplannerStore.findObjectId(wallId);

    if (!sourceWall) return [];

    // Detect wall attachment points
    const detectedPoints = detectAttachmentPoints(wallId, dragPosition);

    // Create attachment point objects
    detectedPoints.forEach((point, index) => {
      attachmentPoints.push({
        id: `${wallId}-attachment-${index}`,
        position: point.position,
        type: point.type,
        visible: true
      });
    });

    return attachmentPoints;
  }, [wallId, dragPosition, performanceMonitor]);
};

/**
 * Detect nearby attachment opportunities for a wall during drag
 */
export const detectAttachmentPoints = (wallId: string, position: THREE.Vector2): Array<{
  position: THREE.Vector2;
  type: 'wall' | 'endpoint';
}> => {
  const attachmentPoints: Array<{ position: THREE.Vector2; type: 'wall' | 'endpoint' }> = [];
  const sourceWall = floorplannerStore.findObjectId(wallId);

  if (!sourceWall || sourceWall.type !== 'wall') return attachmentPoints;
  
  // Type assertion: we know sourceWall is WallType after the check above
  const wall = sourceWall as WallType;

  // Spatial optimization - only check walls within reasonable distance
  const nearbyWalls = floorplannerStore.wallsMap.values();
  const maxDistance = 2.0; // 2 meters

  for (const targetWall of nearbyWalls) {
    if (targetWall.id === wallId) continue;

    // Check if target wall is within reasonable distance
    const distanceToStart = targetWall.start.distanceTo(position);
    const distanceToEnd = targetWall.end.distanceTo(position);

    if (distanceToStart > maxDistance && distanceToEnd > maxDistance) continue;

    // Check for endpoint attachments
    const startAttachment = checkEndpointAttachment(wall, targetWall, targetWall.start);
    if (startAttachment) {
      attachmentPoints.push({
        position: targetWall.start.clone(),
        type: 'endpoint'
      });
    }

    const endAttachment = checkEndpointAttachment(wall, targetWall, targetWall.end);
    if (endAttachment) {
      attachmentPoints.push({
        position: targetWall.end.clone(),
        type: 'endpoint'
      });
    }

    // Check for wall attachments (mid-wall connections)
    const wallAttachment = checkWallAttachment(wall, targetWall, position);
    if (wallAttachment) {
      attachmentPoints.push({
        position: wallAttachment,
        type: 'wall'
      });
    }
  }

  // Remove duplicate points that are very close to each other
  return attachmentPoints.filter((point, index, self) => {
    return self.findIndex(p => p.position.distanceTo(point.position) < 0.05) === index;
  });
};

/**
 * Check if an endpoint attachment is possible
 */
const checkEndpointAttachment = (
  sourceWall: WallType,
  targetWall: WallType,
  endpoint: THREE.Vector2
): boolean => {
  // Check if endpoint is already connected
  const isAlreadyConnected = targetWall.centerConnections?.some(
    conn => conn.id === sourceWall.id
  );

  if (isAlreadyConnected) return false;

  // Check if endpoint is within snapping tolerance
  const tolerance = 0.1; // 10cm
  return sourceWall.start.distanceTo(endpoint) < tolerance ||
         sourceWall.end.distanceTo(endpoint) < tolerance;
};

/**
 * Check if a wall attachment is possible
 */
const checkWallAttachment = (
  sourceWall: WallType,
  targetWall: WallType,
  dragPosition: THREE.Vector2
): THREE.Vector2 | null => {
  // Simple check for wall attachments along the target wall
  const targetLine = new THREE.Line3(
    new THREE.Vector3(targetWall.start.x, targetWall.start.y, 0),
    new THREE.Vector3(targetWall.end.x, targetWall.end.y, 0)
  );

  const dragPoint3D = new THREE.Vector3(dragPosition.x, dragPosition.y, 0);
  const closestPoint = new THREE.Vector3();

  targetLine.closestPointToPoint(dragPoint3D, false, closestPoint);

  const closestPoint2D = new THREE.Vector2(closestPoint.x, closestPoint.y);
  const distance = dragPosition.distanceTo(closestPoint2D);

  // Only suggest attachment if within tolerance
  if (distance < 0.1) {
    return closestPoint2D;
  }

  return null;
};